import { collectTextFontUsage } from '../../elements/text/utils/mixedTypography';
import { getFontMetricOverrides } from '../../elements/text/utils/selectFontMetrics';
import selectFontVariant from './selectFontVariant';

const hasTuple = (tuples, tuple) =>
  tuples.some((value) => value[0] === tuple[0] && value[1] === tuple[1]);

/**
 * Collects provider-aware font definitions for every effective text run.
 * The result is shared by JSX output declarations and URL-only consumers.
 */
export default function getTextFontDefinitions(pages) {
  const definitionsByService = new Map();

  for (const { elements = [] } of pages) {
    for (const element of elements.filter(({ type }) => type === 'text')) {
      for (const usage of collectTextFontUsage(element)) {
        const { font, renderFamily, identityKey, revision } = usage;
        if (!font.service || font.service === 'system') {
          continue;
        }

        const serviceDefinitions =
          definitionsByService.get(font.service) || new Map();
        definitionsByService.set(font.service, serviceDefinitions);
        const family =
          font.service === 'fonts.google.com' ? font.family : renderFamily;
        const key =
          font.service === 'fonts.google.com'
            ? family
            : [family, identityKey, revision || ''].join('|');
        const definition = serviceDefinitions.get(key) || {
          key,
          family,
          variants: [],
          url: font.url,
          urls: font.urls,
          metricOverrides: getFontMetricOverrides(font),
        };
        const requested = [
          usage.fontStyle === 'italic' ? 1 : 0,
          Number(usage.fontWeight) || 400,
        ];
        const selected = selectFontVariant(font.variants || [], requested);
        if (!hasTuple(definition.variants, selected)) {
          definition.variants.push(selected);
        }
        serviceDefinitions.set(key, definition);
      }
    }
  }

  return new Map(
    Array.from(definitionsByService, ([service, definitions]) => [
      service,
      Array.from(definitions.values()),
    ])
  );
}
